import React from 'react';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { Button } from 'antd';
import { ThunkDispatch } from 'redux-thunk';
import { FormattedMessage } from 'react-intl';
import isBefore from 'date-fns/isBefore';
import { RootState } from 'src/store';
import {
  condoFiltersActions,
  condoFlexibleDatePickerActions,
  condoGuestsActions,
  condosActions,
  condosLocationPickerActions,
  condoStrictDatesActions,
  getCondos,
  ICondoFiltersState,
  ICondoFlexibleDatePickerState,
  ICondoGuestsState,
  ICondosLocationPickerState,
  ICondosState,
  ICondoStrictDatesState,
} from 'src/store/slices/index';
import { BlueButton } from 'src/components/components/index';
import { SearchSvg, UpdateSvg } from 'src/assets/index';
import CondoBackground from 'src/assets/images/condo/bg-condo.jpg';
import { disableAnytimeSearchWarning, getDateCheckInOut, getTimeout, Responsive, UrlUtils } from 'src/utils/index';
import {
  CONDO_DATES_FLEXIBLE_LABEL,
  CONDO_DATES_LABEL,
  CONDO_FILTERS_LABEL,
  CONDO_GUESTS_LABEL,
  CONDO_IS_FLEXIBLE_LABEL,
  CONDO_LOCATIONS_LABEL,
  CONDO_LOCATIONS_OBJECT_LABEL,
  CONDO_SEARCH_BOUNDS,
  CONDO_SESSION_KEY,
  DEFAULT_PAGE_NUMBER,
} from 'src/constants/index';
import {
  DateSearchTypeEnum,
  IBounds,
  ICondoLocation,
  ISessionKey,
  SearchTypeEnum,
  StayForEnum,
} from 'src/common-types/index';
import { CondoGuestsPicker } from '../condo-guests-picker';
import { CondoStrictDatePicker } from '../condo-strict-date-picker';
import { CondoSearchInfo } from '../condo-search-info';
import { CondoLocationPicker } from '../condo-location-picker';
import './style.scss';
import { ModalAnytimeSearchWarning } from '../modal-anytime-search-warning';

interface IMapStateToProps {
  condosStore: ICondosState;
  condosLocationPickerStore: ICondosLocationPickerState;
  condoStrictDatesStore: ICondoStrictDatesState;
}

interface IMapDispatchToProps {
  setIsSearch: () => void;
  setPageNumber: () => void;
  setIsAnytimeSearchWarningVisible: (visible: boolean) => void;
  getCondos: (isReuse: boolean) => void;
  setShouldFocus: () => void;
  selectLocationLabel: (label: string) => void;
  selectLocation: (location: ICondoLocation) => void;
  setGuestsState: (state: ICondoGuestsState) => void;
  setFlexibleState: (state: ICondoFlexibleDatePickerState) => void;
  setSearchType: (type: DateSearchTypeEnum) => void;
  setDates: (dates: { startDate: string; endDate: string }) => void;
  setSessionKey: (sessionKey: ISessionKey) => void;
  setBounds: (bounds: IBounds) => void;
  setFilters: (filters: ICondoFiltersState) => void;
  resetFilters: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps { }

interface IState {
  isCollapse: boolean;
}

const zero = 0;
const one = 1;

class CondoSearchComponent extends React.Component<IProps, IState> {
  state: IState = {
    isCollapse: false,
  };

  componentDidMount() {
    const {
      condosStore,
      setDates,
      selectLocation,
      selectLocationLabel,
      setSearchType,
      setGuestsState,
      setFlexibleState,
      setSessionKey,
      setBounds,
      setFilters,
    } = this.props;
    const values = UrlUtils.getValues();
    let isReuse = false;

    if (values[CONDO_FILTERS_LABEL]) {
      setFilters(values[CONDO_FILTERS_LABEL] as ICondoFiltersState);
    }

    if (values[CONDO_SEARCH_BOUNDS]) {
      setBounds(values[CONDO_SEARCH_BOUNDS] as IBounds);
    }

    if (values[CONDO_LOCATIONS_OBJECT_LABEL]) {
      selectLocation(values[CONDO_LOCATIONS_OBJECT_LABEL] as ICondoLocation);
    }

    if (values[CONDO_LOCATIONS_LABEL]) {
      selectLocationLabel(values[CONDO_LOCATIONS_LABEL] as string);
    }

    if (values[CONDO_DATES_LABEL]) {
      const dates: { startDate: string; endDate: string } = values[CONDO_DATES_LABEL] as {
        startDate: string;
        endDate: string;
      };
      const today = new Date();
      today.setHours(zero, zero, zero, zero);

      setDates(dates);

      if (dates?.startDate && isBefore(getDateCheckInOut(dates?.startDate), today)) {
        setDates({ endDate: undefined, startDate: undefined });
        setSearchType(DateSearchTypeEnum.Strict);
      }
    }

    if (values[CONDO_IS_FLEXIBLE_LABEL]) {
      const value = values[CONDO_IS_FLEXIBLE_LABEL];
      setSearchType(
        value.toString() === `${one}` ? DateSearchTypeEnum.Flexible : DateSearchTypeEnum.Strict,
      );
    }
    if (values[CONDO_DATES_FLEXIBLE_LABEL]) {
      const selectedData: ICondoFlexibleDatePickerState = values[
        CONDO_DATES_FLEXIBLE_LABEL
      ] as ICondoFlexibleDatePickerState;
      const today = new Date();
      today.setHours(zero, zero, zero, zero);
      today.setDate(one);
      const months = selectedData?.selectedMonths.filter((dateString: string) => {
        const date = new Date(dateString);
        date.setHours(zero, zero, zero, zero);
        date.setDate(one);

        return !isBefore(date, today);
      });

      setFlexibleState({
        stayFor: selectedData?.stayFor,
        selectedMonths: months?.length ? months : [],
      });
    }

    if (!values[CONDO_DATES_FLEXIBLE_LABEL]) {
      setFlexibleState({
        stayFor: StayForEnum.Week,
        selectedMonths: [],
      });
    }

    if (values[CONDO_GUESTS_LABEL]) {
      setGuestsState(values[CONDO_GUESTS_LABEL] as ICondoGuestsState);
    }

    const session: ISessionKey = values[CONDO_SESSION_KEY] as ISessionKey;

    if (session && session.expireDate && getTimeout(session.expireDate) > zero) {
      setSessionKey(session);
      isReuse = true;
    }

    setTimeout(() => {
      if (!condosStore.condos && session) {
        this.onSearch({ isReuse });
      }
    });
  }

  onAnytimeSearchWarningClose = (e: React.MouseEvent<HTMLElement, MouseEvent>, dontShowAgain: boolean) => {
    if (dontShowAgain) {
      disableAnytimeSearchWarning();
    }
    this.props.setIsAnytimeSearchWarningVisible(false);
  }

  onSearch = ({ isReuse = false, isInit = true } = {}) => {
    const {
      setIsSearch,
      getCondos,
      condosLocationPickerStore,
      setShouldFocus,
      setPageNumber,
      resetFilters,
      condoStrictDatesStore,
      setSearchType
    } = this.props;
    const { shouldFocus, selectedLocation } = condosLocationPickerStore;

    if (condoStrictDatesStore.searchType === DateSearchTypeEnum.Flexible) {
      setSearchType(DateSearchTypeEnum.Flexible);
    }

    if (!shouldFocus && selectedLocation) {
      setPageNumber();
      setIsSearch();
      this.setState({ ...this.state, isCollapse: !this.state.isCollapse });

      if (!isInit) {
        resetFilters();
        UrlUtils.setUrl(CONDO_FILTERS_LABEL, null);
      }

      getCondos(isReuse);
      const contentWrapper = document.querySelector('.condo-result-wrapper__content');

      if (contentWrapper) {
        contentWrapper.scroll(zero, zero);
        window.scrollTo(zero, zero);
      }
    } else {
      if (!isInit) {
        setShouldFocus();
      }
    }
  };

  onToggle = () => {
    this.setState({ ...this.state, isCollapse: !this.state.isCollapse });
  };

  render(): React.ReactNode {
    const { isSearch, isAnytimeSearchWarningVisible } = this.props.condosStore;
    const { isCollapse } = this.state;

    return (
      <div
        className={`condo-search  ${isSearch ? 'after-search' : ''} ${isCollapse ? 'toggle-condo-search-info' : ''
          }`}
        style={{ backgroundImage: !isSearch && `url(${CondoBackground})` }}
      >
        <div className="condo-search__wrapper">
          <h1 className="condo-search__header">
            <FormattedMessage id="condo.header" />
          </h1>
          <div className="condo-search__header-small">
            <FormattedMessage id="condo.header.small" />
          </div>
          <div className="condo-search__search-section">
            <div className="condo-search__search-top">
              <CondoLocationPicker />
              <Responsive>
                <CondoStrictDatePicker />
              </Responsive>
              <CondoGuestsPicker />
              <BlueButton onClick={() => this.onSearch({ isInit: false })}>
                {isSearch ? (
                  <>
                    <UpdateSvg />
                    <FormattedMessage id="update.search.your.hotel" />
                  </>
                ) : (
                  <>
                    <SearchSvg />
                    <FormattedMessage id="find.your.condo" />
                  </>
                )}
              </BlueButton>
            </div>
          </div>
          {isCollapse && <CondoSearchInfo onToggle={this.onToggle} />}
          {isSearch && !isCollapse && (
            <Button
              className="condo-search__toggle-condo-search-info"
              onClick={this.onToggle}
              type="text"
            >
              <FormattedMessage id="cancel" />
            </Button>
          )}
        </div>
        <ModalAnytimeSearchWarning
          visible={isAnytimeSearchWarningVisible}
          onCancel={this.onAnytimeSearchWarningClose} />
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condosStore: state.condosStore,
    condosLocationPickerStore: state.condosLocationPickerStore,
    condoStrictDatesStore: state.condoStrictDatesStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setIsSearch: () => {
    dispatch(condosActions.setIsSearch(true));
  },
  setSessionKey: (sessionKey: ISessionKey) => {
    dispatch(condosActions.setSessionKey(sessionKey));
  },
  setPageNumber: () => {
    dispatch(condosActions.setPageNumber(DEFAULT_PAGE_NUMBER));
  },
  setIsAnytimeSearchWarningVisible: (visible: boolean) => {
    dispatch(condosActions.setIsAnytimeSearchWarningVisible(visible));
  },
  selectLocationLabel: (label: string) => {
    dispatch(condosLocationPickerActions.selectLocationLabel(label));
  },
  selectLocation: (location: ICondoLocation) => {
    dispatch(condosLocationPickerActions.selectLocation(location));
  },
  setGuestsState: (state: ICondoGuestsState) => {
    dispatch(condoGuestsActions.setState(state));
  },
  setFlexibleState: (state: ICondoFlexibleDatePickerState) => {
    dispatch(condoFlexibleDatePickerActions.setState(state));
  },
  setSearchType: (type: DateSearchTypeEnum) => {
    dispatch(condoStrictDatesActions.setSearchType(type));
  },
  setDates: (dates: { startDate: string; endDate: string }) => {
    dispatch(condoStrictDatesActions.setDates(dates));
  },
  setBounds: (bounds: IBounds) => {
    dispatch(condosActions.setBounds(bounds));
  },
  getCondos: (isReuse: boolean) => {
    dispatch(getCondos(SearchTypeEnum.NewSearch, isReuse));
  },
  setShouldFocus: () => {
    dispatch(condosLocationPickerActions.setShouldFocus(true));
  },
  setFilters: (filters: ICondoFiltersState) => {
    dispatch(condoFiltersActions.setFilters(filters));
  },
  resetFilters: () => {
    dispatch(condoFiltersActions.resetFilters());
  },
});

export const CondoSearch = connect(mapStateToProps, mapDispatchToProps)(CondoSearchComponent);
